import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import EditButton from 'components/v2/buttons/EditButton';
import Spinner from 'components/ui/Spinner';
import Card from 'components/v2/panels/Card';
import SessionStore from 'stores/SessionStore';
import { getStopDocumentsConfig } from 'utils/UserServiceAPIUtils';
import EditShipperRulesModal from './EditStopDocumentsModal';

const MOVEMENT_TYPES = [
  'IMPORT_DRAYAGE',
  'IMPORT_RAIL',
  'EXPORT_DRAYAGE',
  'EXPORT_RAIL',
  'REPOSITION_DRAYAGE',
  'REPOSITION_RAIL',
  'DOMESTIC_DRAYAGE',
  'DOMESTIC_RAIL',
];

const STOP_TYPES = [
  'LOADED_PICKUP',
  'LOADED_DELIVERY',
  'STOP_EMPTY_PICKUP',
  'STOP_EMPTY_DELIVERY',
  'OFFSITE_LOADED_PICKUP',
  'OFFSITE_LOADED_DELIVERY',
  'OFFSITE_EMPTY_PICKUP',
  'OFFSITE_EMPTY_DELIVERY',
];

function StopDocumentRequirementsGroup({
  className,
  group,
  handleClickEdit,
  canEdit,
}) {
  const { t } = useTranslation();

  return (
    <div className={className}>
      <h3 className="mb-4 text-base font-semibold leading-6 text-gray-900">
        {t(group.movementType)}
      </h3>
      <Card>
        <div className="divide-y divide-gray-100">
          {group.stopTypes.map((stopType) => {
            return (
              <div className="py-4" key={stopType.name}>
                <div className="flex items-center justify-between">
                  <p className="font-medium text-gray-900">
                    {t(stopType?.name?.toUpperCase())}
                  </p>
                  <EditButton
                    size="small"
                    disabled={!canEdit}
                    onClick={() => {
                      handleClickEdit(group.movementType, stopType);
                    }}
                  />
                </div>
                {stopType.docTypes.map((docType) => (
                  <div className="py-2 text-sm text-gray-500" key={docType}>
                    {t(docType?.toUpperCase())}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}

export default function StopDocumentRequirements({ id }) {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [groups, setGroups] = useState([]);
  const [userStopConfigs, setUserStopConfigs] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [currentMovement, setCurrentMovement] = useState('');
  const [currentStopType, setCurrentStopType] = useState({});
  const canEditSettings = SessionStore.hasShipperOnboardingRole();
  function renderSpinner() {
    return <Spinner showMessage message={t('LOADING')} />;
  }

  function toggleModal() {
    setShowModal(!showModal);
  }

  function handleClickEdit(movementType, stopType) {
    setCurrentMovement(movementType);
    setCurrentStopType(stopType);
    toggleModal();
  }

  function getDocumentRequirements(stopConfigs, movementType, stopType) {
    const [movement, shipmentType] = movementType.split('_');
    const offsite = stopType.includes('OFFSITE');
    const empty = stopType.includes('EMPTY');
    const actionType = stopType.includes('PICKUP') ? 'PICKUP' : 'DELIVER';
    const foundConfig = stopConfigs?.find(
      (config) =>
        config.movementType === movement &&
        config.shipmentType === shipmentType &&
        config.offsite === offsite &&
        config.empty === empty &&
        config.actionType === actionType,
    );

    const docs = foundConfig?.stopDocuments;
    return docs != null ? docs : [];
  }

  function transformData() {
    const mappedGroups = MOVEMENT_TYPES.map((movementType) => {
      const group = {
        movementType,
      };

      group.stopTypes = STOP_TYPES.map((stopType) => {
        return {
          name: stopType,
          docTypes: getDocumentRequirements(
            userStopConfigs,
            movementType,
            stopType,
          ),
        };
      });

      return group;
    });

    return mappedGroups;
  }

  const fetchGroupedStopDocuments = async (id) => {
    try {
      const userResponse = await getStopDocumentsConfig(id);
      if (!userResponse) {
        throw new Error('No response from API');
      }
      const configs =
        userResponse?.data?.getStopDocumentsConfigQuery?.stopConfigurations ??
        [];
      setUserStopConfigs(configs);
    } catch (err) {
      console.error('Error during getStopDocumentsConfig: ', err.message);
    }
  };

  useEffect(() => {
    setIsLoading(true);
    fetchGroupedStopDocuments(id);
  }, []);

  useEffect(() => {
    setGroups(transformData());
    setIsLoading(false);
  }, [userStopConfigs]);

  useEffect(() => {
    if (!showModal) {
      fetchGroupedStopDocuments(id);
    }
  }, [showModal]);

  return isLoading ? (
    renderSpinner()
  ) : (
    <div className="w-full">
      <div className="md:grid lg:grid-cols-2 xl:grid-cols-3 md:gap-4">
        {groups.map((group) => (
          <StopDocumentRequirementsGroup
            key={group.movementType}
            group={group}
            handleClickEdit={handleClickEdit}
            canEdit={canEditSettings}
          />
        ))}
      </div>
      {showModal && (
        <EditShipperRulesModal
          currentMovement={currentMovement}
          currentStop={currentStopType}
          onHide={toggleModal}
          id={id}
        />
      )}
    </div>
  );
}
